import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { useHistory } from 'react-router'
import { useLocation } from 'react-router-dom'
import ContentError from 'components/ContentError'
import ContentLoading from 'components/ContentLoading'
import PtinRequiredView from 'components/PtinRequiredView'
import {
  StickyViewHeader,
  StickyViewHeaderExtras,
  StickyViewHeaderMain,
} from 'components/StickyViewHeader'
import WorkspaceFilterViewHeading from 'components/WorkspaceFilterViewHeading'
import { useDocumentTitle } from 'hooks/document-title'
import { useOnEscDown } from 'hooks/key-press'
import { useActivation } from 'hooks/mobx-hooks'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import JobFilterViewHeading from 'jobs/components/JobFilterViewHeading'
import { SearchState } from 'jobs/infra/JobSearchViewModel'
import { AutomatedAction } from 'opportunities/offers/OpportunityOffersViewModel'
import JobManagementViewModel from './JobManagementViewModel'
import JobsViewer from './JobsViewer'
import styles from './JobManagement.sass'

interface JobManagementProps extends React.HTMLAttributes<HTMLDivElement> {
  vm: JobManagementViewModel
}

export default observer(function JobManagement({ vm }: JobManagementProps) {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  const searchState = vm.searcher.searchState
  const workspacesFilter = vm.searcher.workspacesFilter

  const location = useLocation()
  const history = useHistory()

  React.useEffect(() => {
    const qs = new URLSearchParams(location.search)
    const action = qsToAcceptDecline(qs)

    if (action) {
      vm.handleOfferResponse(action).then()

      qs.delete('acceptOffer')
      qs.delete('declineOffer')

      history.replace({ search: qs.toString() })
    }
  }, [location.search])

  useDocumentTitle('Job Management')
  useActivation(vm)
  useOnEscDown(vm.searcher.searchText.clear)

  if (vm.ptinRequired) {
    return <PtinRequiredView />
  }

  return (
    <div className={cx(styles.root)} ref={scrollRef}>
      {searchState === SearchState.InitialSearchRunning ? (
        <ContentLoading />
      ) : (
        <>
          <StickyViewHeader scrollOffset={scrollOffset}>
            <StickyViewHeaderMain>
              <WorkspaceFilterViewHeading
                allOptionText="All workspaces"
                primaryText="Job management"
                filter={workspacesFilter}
              />
            </StickyViewHeaderMain>

            <StickyViewHeaderExtras>
              <JobFilterViewHeading vm={vm.searcher} />
            </StickyViewHeaderExtras>
          </StickyViewHeader>

          <div className={styles.content}>
            {vm.activate.match({
              pending: () => <ContentLoading />,
              rejected: (err) => <ContentError error={err} />,
              resolved: () => <JobsViewer vm={vm} />,
            })}
          </div>
        </>
      )}
    </div>
  )
})

/**
 * Maps the query parameter to a well-typed "Automated Action" union.
 * @param qs
 */
function qsToAcceptDecline(qs: URLSearchParams): AutomatedAction | null {
  if (qs.has('acceptOffer')) {
    return { type: 'Accept', id: qs.get('acceptOffer')! }
  } else if (qs.has('declineOffer')) {
    return { type: 'Decline', id: qs.get('declineOffer')! }
  }

  return null
}
